import axios, { type InternalAxiosRequestConfig } from "axios";
import {
  logPaymentError,
  getPaymentErrorSource,
  type PaymentFlow,
} from "../utils/paymentErrorLogger";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "../auth/authStorage";
import { refreshToken as refreshTokenApi } from "../auth/authApi";

const baseURL = process.env.REACT_APP_API_URL;

if (!baseURL) {
  throw new Error("REACT_APP_API_URL is not defined in the .env file");
}

const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

function isPaymentRequest(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes("/Payment/") ||
    url.includes("/SubscriptionPayment/") ||
    url.includes("/payment/") ||
    url.includes("/subscriptionpayment/")
  );
}

function getPaymentFlowFromUrl(url?: string): PaymentFlow {
  if (!url) return "payment-api";
  if (url.includes("SubscriptionPayment")) return "membership";
  if (url.includes("Payment")) return "payment-api";
  return "payment-api";
}

/** User-facing validation (e.g. invalid Stripe promo on preview) — show in UI only, do not POST to report-client. */
function shouldOmitClientPaymentErrorReport(error: {
  config?: { url?: string };
  response?: { status?: number };
}): boolean {
  const url = (error.config?.url || "").toLowerCase();
  const status = error.response?.status;
  if (status == null || status >= 500) return false;
  if (url.includes("preview-wallet-pricing") && status >= 400 && status < 500) {
    return true;
  }
  return false;
}

let consecutive401Count = 0;
let refreshInFlight: Promise<string> | null = null;

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (
      error.config &&
      isPaymentRequest(error.config?.url) &&
      !shouldOmitClientPaymentErrorReport(error)
    ) {
      logPaymentError({
        flow: getPaymentFlowFromUrl(error.config?.url),
        errorSource: "frontend",
        message: "Payment request interceptor error (e.g. payload/header)",
        error,
        requestConfig: error.config
          ? {
              url: error.config.url,
              method: error.config.method,
              baseURL: error.config.baseURL,
              headers: error.config.headers as Record<string, string>,
              params: error.config.params,
            }
          : undefined,
        requestPayload: error.config?.data,
      });
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    consecutive401Count = 0;
    return response;
  },
  async (error) => {
    if (
      error.config &&
      isPaymentRequest(error.config?.url) &&
      !shouldOmitClientPaymentErrorReport(error)
    ) {
      const errorSource = getPaymentErrorSource(error);
      const message =
        error.response?.data?.error ||
        error.message ||
        (errorSource === "network" ? "Network error" : "Payment API error");
      try {
        logPaymentError({
          flow: getPaymentFlowFromUrl(error.config?.url),
          errorSource,
          message: `Payment API failed: ${message}`,
          error,
          requestConfig: error.config
            ? {
                url: error.config.url,
                method: error.config.method,
                baseURL: error.config.baseURL,
                headers: error.config.headers as Record<string, string>,
                params: error.config.params,
              }
            : undefined,
          requestPayload: error.config?.data,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        });
      } catch (e) {
        console.error("[PaymentError] Failed to log payment response error", e);
      }
    }

    if (error.response?.status === 401) {
      const originalRequest = error.config;
      const alreadyRetried = originalRequest?._retry === true;

      // Try a single refresh flow before declaring session expired
      if (originalRequest && !alreadyRetried) {
        originalRequest._retry = true;
        const rt = getRefreshToken();
        if (rt) {
          try {
            if (!refreshInFlight) {
              refreshInFlight = (async () => {
                const refreshed = await refreshTokenApi(rt);
                setTokens({
                  accessToken: refreshed.accessToken,
                  refreshToken: refreshed.refreshToken,
                  expiresAtUtc: refreshed.expiresAtUtc,
                  userObjectId: refreshed.userObjectId,
                  email: refreshed.email,
                });
                return refreshed.accessToken;
              })().finally(() => {
                refreshInFlight = null;
              });
            }

            const newAccessToken = await refreshInFlight;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosInstance.request(originalRequest);
          } catch (refreshErr) {
            clearTokens();
            window.dispatchEvent(new CustomEvent("sessionExpired"));
            return Promise.reject(refreshErr);
          }
        }
      }

      consecutive401Count += 1;
      if (consecutive401Count >= 2) {
        clearTokens();
        window.dispatchEvent(new CustomEvent("sessionExpired"));
      }
    } else {
      consecutive401Count = 0;
    }

    return Promise.reject(error);
  }
);

export const getAPIURL = (endpoint: string) => `${baseURL}${endpoint}`;
export default axiosInstance;
